import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';

import { chooseFileSaga, processFileSaga, uploadDocumentSaga } from '../../utils/docUpload';
import { get, post } from '../../utils/request';
import {
  changeStep,
  chooseDocumentCancellation,
  chooseDocumentFailure,
  chooseDocumentSuccess,
  fetchHelocDrawFailure,
  fetchHelocDrawSuccess,
  fetchLoanFailure,
  fetchLoanSuccess,
  fetchMortgageFailure,
  fetchMortgageSuccess,
  fetchTransactionDetailFailure,
  fetchTransactionDetailSuccess,
  fetchTransactionsFailure,
  fetchTransactionsSuccess,
  processDocument,
  processDocumentFailure,
  processDocumentSuccess,
  setIsHelocDrawAvailable,
  submitHelocDrawFailure,
  submitHelocDrawSuccess,
  submitHelocDrawValidationFailure,
  updateFormError,
  updateHelocDrawAmount,
  uploadDocumentFailure,
  uploadDocumentSuccess,
  validateRoutingFailure,
  validateRoutingSuccess,
} from './actions';
import {
  CHOOSE_DOCUMENT,
  FETCH_ERROR_MESSAGE,
  FETCH_HELOC_DRAW,
  FETCH_LOAN,
  FETCH_MORTGAGE,
  FETCH_TRANSACTIONS,
  FETCH_TRANSACTION_DETAIL,
  FETCH_TRANSACTION_DETAIL_ERROR_MESSAGE,
  HELOC_DRAW_API_REASON_CODE_DICTIONARY,
  HELOC_DRAW_PARTIAL_CREATE_ERRORS,
  HELOC_DRAW_PROPS_DICTIONARY,
  PROCESS_DOCUMENT,
  SUBMIT_HELOC_DRAW,
  UPLOAD_DOCUMENT,
  VALIDATE_ROUTING,
} from './constants';
import { getHelocDrawStatusBanner } from './helpers';
import makeSelectMain from '../Main/selectors';
import makeSelectMobile from '../Mobile/selectors';
import makeSelectMortgage from './selectors';

export function* fetchLoanSaga({ baseUrl }, { payload }) {
  try {
    const { loanNumber } = payload;
    const { loanDetails } = yield call(get, `${baseUrl}/loans/${loanNumber}`);
    const { summary } = loanDetails;
    yield put(fetchLoanSuccess(summary));
  } catch (error) {
    yield put(fetchLoanFailure(FETCH_ERROR_MESSAGE));
  }
}

export function* fetchTransactionDetailSaga({ baseUrl }, { payload }) {
  try {
    const { transactionId } = payload;
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const queryParams = `transactionId=${transactionId}`;
    const endPoint = `${baseUrl}/transactions/detail/${loanNumber}?${queryParams}`;
    const { error, transaction } = yield call(get, endPoint);
    if (error) {
      yield put(fetchTransactionDetailFailure(FETCH_TRANSACTION_DETAIL_ERROR_MESSAGE));
    } else {
      yield put(fetchTransactionDetailSuccess(transaction));
    }
  } catch (error) {
    yield put(fetchTransactionDetailFailure(FETCH_TRANSACTION_DETAIL_ERROR_MESSAGE));
  }
}

export function* fetchTransactionsSaga({ baseUrl }) {
  try {
    const pageToFetch = yield select(makeSelectMortgage('pageToFetch'));
    // if pageToFetch is -1, all pages have already been fetched
    if (pageToFetch !== -1) {
      const loanNumber = yield select(makeSelectMain('selectedLoan'));
      const itemsPerPage = yield select(makeSelectMortgage('itemsPerPage'));
      const queryParams =
        `itemsPerPage=${itemsPerPage}&pageToFetch=${pageToFetch}&transactionType=escrow`;
      const endPoint = `${baseUrl}/transactions/${loanNumber}?${queryParams}`;
      const { page, totalPages, transactions } = yield call(get, endPoint);
      yield put(fetchTransactionsSuccess(page, totalPages, transactions));
    }
  } catch (error) {
    yield put(fetchTransactionsFailure(FETCH_ERROR_MESSAGE));
  }
}

export function* fetchMortgageSaga({ baseUrl }, { payload: { loanNumber } }) {
  try {
    const mortgageInfo = yield call(get, `${baseUrl}/mortgage/${loanNumber}`);
    yield put(fetchMortgageSuccess({ mortgageInfo }));
  } catch (error) {
    yield put(fetchMortgageFailure(FETCH_ERROR_MESSAGE));
  }
}

export function* fetchHelocDrawSaga({ baseUrl }, { payload: { loanNumber } }) {
  try {
    const {
      errorCode,
      statusData,
    } = yield call(get, `${baseUrl}/mortgage/${loanNumber}/helocdraw/status`);
    if (errorCode) {
      yield put(setIsHelocDrawAvailable());
    } else {
      const {
        allowedOperation,
        currentSetting: {
          allowedRoutingNumbers,
          canUseAnyRoutingNumber,
          creditLimitAmount,
          currentAvailableAmount,
          isAccountRequired,
          minimumAdvanceAmount,
        },
        status,
      } = statusData;
      const statusBanner = getHelocDrawStatusBanner(status);
      yield put(fetchHelocDrawSuccess({
        allowedOperation,
        allowedRoutingNumbers,
        availableAdvanceAmount: currentAvailableAmount,
        canUseAnyRoutingNumber,
        creditLineLimit: creditLimitAmount,
        isAccountRequired,
        minimumAdvanceAmount,
        statusBanner,
      }));
    }
  } catch (error) {
    yield put(fetchHelocDrawFailure());
  }
}

export function* submitHelocDrawSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload }) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const {
      accountNumber,
      accountType,
      amount,
      loanNumber,
      nameOnAccount,
      routingNumber,
    } = payload;
    const endpoint = `${baseUrl}/mortgage/${loanNumber}/helocdraw/create`;
    const { confirmationNumber, error } = yield call(post, endpoint, {
      accountNumber,
      accountType,
      amount,
      nameOnAccount,
      routingNumber,
    });
    const properNavigationHandler = isNativeMobile ?
      navigationReplaceHandler :
      navigationHandler;
    if (error) {
      const { reasons } = error;
      if (reasons.every((reason) => HELOC_DRAW_PARTIAL_CREATE_ERRORS.includes(reason))) {
        const { field, message } = HELOC_DRAW_API_REASON_CODE_DICTIONARY[reasons[0]];
        yield put(submitHelocDrawValidationFailure({ field, message }));
        if (isMobile) {
          properNavigationHandler('HelocForm');
        }
        return;
      }
      throw new Error();
    } else {
      const requestAmount = amount;
      const requestDate = new Date();
      yield put(submitHelocDrawSuccess({
        confirmationNumber,
        requestAmount,
        requestDate,
      }));
      if (isMobile) {
        properNavigationHandler('HelocConfirmation');
      } else {
        yield put(changeStep(3));
      }
    }
  } catch (error) {
    const { subHeading, textString } = HELOC_DRAW_PROPS_DICTIONARY.error;
    yield put(submitHelocDrawFailure(`${subHeading}, ${textString}`));
    if (isMobile) {
      const properNavigationHandler = isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('HelocConfirmation');
    }
  }
}

export function* validateRoutingSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    const properNavigationHandler = isNativeMobile ?
      navigationReplaceHandler :
      navigationHandler;
    const { bankName } = yield call(get, `${baseUrl}/utilities/${payload.routingNumber}`);

    if (!bankName) {
      yield put(updateFormError({
        field: 'routingNumber',
        form: 'helocDraw',
        value: 'Routing Number is invalid',
      }));
      yield put(validateRoutingSuccess(false));
    } else {
      yield put(validateRoutingSuccess({
        isValid: true,
        routingNumber: payload.routingNumber,
      }));
      yield put(updateHelocDrawAmount({ amount: payload.value }));
      if (isMobile) {
        properNavigationHandler('HelocReview');
      } else {
        yield put(changeStep(2));
      }
    }
  } catch {
    yield put(validateRoutingFailure());
  }
}

export default (options) => function* watcherSaga() {
  yield all([
    takeLatest(
      CHOOSE_DOCUMENT,
      chooseFileSaga,
      {
        ...options,
        chooseDocumentCancellation,
        chooseDocumentFailure,
        chooseDocumentSuccess,
        processDocument,
      },
    ),
    takeLatest(FETCH_HELOC_DRAW, fetchHelocDrawSaga, options),
    takeLatest(FETCH_LOAN, fetchLoanSaga, options),
    takeLatest(FETCH_MORTGAGE, fetchMortgageSaga, options),
    takeLatest(FETCH_TRANSACTIONS, fetchTransactionsSaga, options),
    takeLatest(FETCH_TRANSACTION_DETAIL, fetchTransactionDetailSaga, options),
    takeLatest(
      PROCESS_DOCUMENT,
      processFileSaga,
      {
        ...options,
        processDocumentFailure,
        processDocumentSuccess,
      },
    ),
    takeLeading(SUBMIT_HELOC_DRAW, submitHelocDrawSaga, options),
    takeLatest(
      UPLOAD_DOCUMENT,
      uploadDocumentSaga,
      {
        ...options,
        uploadDocumentFailure,
        uploadDocumentSuccess,
      },
    ),
    takeLeading(VALIDATE_ROUTING, validateRoutingSaga, options),
  ]);
};
